import React, { useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { message } from 'antd';

const formattedContainer = {
  flex: 1,
  overflow: 'auto',
  padding: '32px 40px',
  background: '#fff',
  border: '1px solid rgb(221, 226, 233)',
  borderRadius: '4px',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontSize: '14px',
  lineHeight: 1.7,
  color: '#1d1d1f',
  maxWidth: '100%',
};

const sourceContainer = {
  flex: 1,
  overflow: 'auto',
  padding: '24px',
  background: '#fafafa',
  border: '1px solid rgb(221, 226, 233)',
  borderRadius: '4px',
  position: 'relative',
};

const sourceText = {
  fontFamily: '"SF Mono", "Fira Code", "Fira Mono", Menlo, Consolas, "DejaVu Sans Mono", monospace',
  fontSize: '13px',
  lineHeight: 1.6,
  color: '#333',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  margin: 0,
};

const copyButton = {
  position: 'absolute',
  top: '12px',
  right: '12px',
  padding: '4px 12px',
  fontSize: '12px',
  background: '#fff',
  border: '1px solid #d9d9d9',
  borderRadius: '4px',
  cursor: 'pointer',
  color: '#595959',
  zIndex: 1,
};

const markdownStyles = `
  .md-viewer h1 { font-size: 24px; font-weight: 600; margin: 24px 0 12px; border-bottom: 1px solid #e8e8e8; padding-bottom: 8px; }
  .md-viewer h2 { font-size: 20px; font-weight: 600; margin: 20px 0 10px; }
  .md-viewer h3 { font-size: 16px; font-weight: 600; margin: 16px 0 8px; }
  .md-viewer h4 { font-size: 14px; font-weight: 600; margin: 12px 0 6px; }
  .md-viewer p { margin: 0 0 12px; }
  .md-viewer ul, .md-viewer ol { margin: 0 0 12px; padding-left: 24px; }
  .md-viewer li { margin-bottom: 4px; }
  .md-viewer table { border-collapse: collapse; width: 100%; margin: 12px 0; font-size: 13px; }
  .md-viewer th, .md-viewer td { border: 1px solid #e8e8e8; padding: 8px 12px; text-align: left; }
  .md-viewer th { background: #fafafa; font-weight: 600; }
  .md-viewer tr:nth-child(even) { background: #fafbfc; }
  .md-viewer blockquote { border-left: 3px solid #d9d9d9; margin: 12px 0; padding: 8px 16px; color: #595959; background: #fafafa; }
  .md-viewer code { font-family: "SF Mono", Menlo, Consolas, monospace; font-size: 13px; background: #f5f5f5; padding: 2px 6px; border-radius: 3px; }
  .md-viewer pre { background: #f5f5f5; padding: 16px; border-radius: 4px; overflow-x: auto; margin: 12px 0; }
  .md-viewer pre code { background: none; padding: 0; }
  .md-viewer hr { border: none; border-top: 1px solid #e8e8e8; margin: 20px 0; }
  .md-viewer img { max-width: 100%; }
  .md-viewer a { color: #1677ff; text-decoration: none; }
  .md-viewer a:hover { text-decoration: underline; }
`;

const MarkdownViewer = ({ content, mode }) => {
  const [copied, setCopied] = useState(false);

  if (!content) {
    return (
      <div style={{ ...formattedContainer, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8c8c8c' }}>
        No extracted content available for this document.
      </div>
    );
  }

  if (mode === 'source') {
    return (
      <div style={sourceContainer}>
        <button
          style={copyButton}
          onClick={() => {
            // clipboard.writeText rejects in non-secure contexts, when the
            // permission is denied, or if the clipboard API is unavailable —
            // always surface that to the user rather than leaving an
            // unhandled rejection.
            const clipboard = navigator.clipboard;
            const writePromise = clipboard && clipboard.writeText
              ? clipboard.writeText(content)
              : Promise.reject(new Error('Clipboard API unavailable'));

            writePromise
              .then(() => {
                setCopied(true);
                message.success('Copied to clipboard');
                setTimeout(() => setCopied(false), 2000);
              })
              .catch(() => {
                message.error('Unable to copy — check browser permissions or copy manually.');
              });
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
        <pre style={sourceText}>{content}</pre>
      </div>
    );
  }

  return (
    <>
      <style>{markdownStyles}</style>
      <div style={formattedContainer} className="md-viewer">
        <Markdown remarkPlugins={[remarkGfm]}>{content}</Markdown>
      </div>
    </>
  );
};

export default MarkdownViewer;
